import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ObserveSession from './pages/ObserveSession';
import ReportView from './pages/ReportView';
import MaintenancePage from './components/MaintenancePage';
import { setSentryUser } from './sentry';

const IS_DEV_MODE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';



// On boot, if we have a JWT, decode it once and attach the user to Sentry
// so errors that fire before the user navigates away still carry context.
function rehydrateSentryUserFromToken() {
  try {
    const tok = localStorage.getItem('admin_token');
    if (!tok) return;
    const payload = JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    setSentryUser({
      user_id: payload.user_id,
      email: payload.email || payload.sub,
      role: payload.role,
      organization_id: payload.organization_id,
    });
  } catch { /* malformed token — leave Sentry user unset */ }
}
rehydrateSentryUserFromToken();




// Cross-subdomain SSO handoff. When the portal (aietafirst.com) finishes
// registration / login, it appends #token=<jwt> to the redirect URL.
// Fragments are NEVER sent in the HTTP request, so the token doesn't leak
// into nginx access logs or referer headers. We read it once on first load,
// stash to localStorage, and strip the fragment from the URL.
function bootstrapTokenFromFragment() {
  if (typeof window === 'undefined') return;
  const m = window.location.hash.match(/token=([^&]+)/);
  if (!m) return;
  try {
    const token = decodeURIComponent(m[1]);
    if (token.split('.').length === 3) {
      localStorage.setItem('admin_token', token);
    }
  } catch { /* malformed fragment — ignore */ }
  // Strip the fragment so a copy/paste of the URL doesn't carry the JWT.
  const cleanUrl = window.location.pathname + window.location.search;
  window.history.replaceState({}, '', cleanUrl);
}
bootstrapTokenFromFragment();
rehydrateSentryUserFromToken();   // re-decode now that we may have a token from the fragment

function isMaintenanceTime() {
  const now = new Date();
  const istMinutes = now.getUTCHours() * 60 + now.getUTCMinutes() + 330;
  const istHour = Math.floor((istMinutes / 60) % 24);
  return istHour < 10 || istHour >= 18;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('admin_token');
  return token ? children : <Navigate to="/" />;
}

export default function App() {
  if (!IS_DEV_MODE && process.env.REACT_APP_DISABLE_MAINTENANCE !== 'true' && isMaintenanceTime()) {
    return <MaintenancePage />;
  }

  return (
    <Router basename={process.env.PUBLIC_URL || undefined}>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/observe/:sessionId"
          element={
            <ProtectedRoute>
              <ObserveSession />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report/:sessionId"
          element={
            <ProtectedRoute>
              <ReportView />
            </ProtectedRoute>
          }
        />
        <Route path="/reset-password" element={<Login />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}